
"use client";
import type { SiteData, ActionButton, ReportAbuseFormData } from '@/lib/types';
import Image from 'next/image';
import Link from 'next/link';
import Script from 'next/script'; 
import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ReportAbuseFormDataSchema } from '@/lib/types';
import { submitReportAbuse } from '@/lib/actions/report-abuse-actions';
import { toast } from '@/hooks/use-toast';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel"
import Autoplay from "embla-carousel-autoplay"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  MapPin,
  Phone,
  Mail,
  Instagram,
  Facebook,
  Linkedin,
  Twitter,
  Youtube,
  Globe,
  Eclipse,
  ExternalLink,
  ChevronRight,
  MessageSquare,
  MessageCircle,
  Send,
  Share2,
  Eye,
  EyeOff,
  Award,
  Zap,
  ThumbsUp,
  ShieldCheck,
  Lightbulb,
  Users,
  Clock,
  TrendingUp,
  Star,
  Heart,
  CircleUserRound,
  type LucideIcon,
  Image as ImageIconLucide,
  Menu as MenuIcon,
  Smartphone,
  LocateFixed,
  Clock10,
  X as XIcon,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { MiniSiteContactForm } from '@/components/mini-site-templates/template1/ContactForm'; // Reutilizando
import { cn, isValidImageUrl } from "@/lib/utils";

// Scroll Reveal Wrapper Component
interface ScrollRevealWrapperProps {
  children: ReactNode;
  className?: string;
  delay?: 'delay-0' | 'delay-75' | 'delay-100' | 'delay-150' | 'delay-200' | 'delay-300' | 'delay-500' | 'delay-700' | 'delay-1000';
  initialTranslateY?: 'translate-y-5' | 'translate-y-10' | 'translate-y-20';
  duration?: 'duration-300' | 'duration-500' | 'duration-700' | 'duration-1000';
  threshold?: number;
}

const ScrollRevealWrapper: React.FC<ScrollRevealWrapperProps> = ({
  children,
  className,
  delay = 'delay-100',
  initialTranslateY = 'translate-y-10',
  duration = 'duration-700',
  threshold = 0.1,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentRef = sectionRef.current; 
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold }
    );

    observer.observe(currentRef);
    return () => {
      if (currentRef) { 
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return (
    <div
      ref={sectionRef}
      className={cn(
        'transition-all ease-out',
        duration,
        delay,
        isVisible ? 'opacity-100 translate-y-0' : `opacity-0 ${initialTranslateY}`,
        className
      )}
    >
      {children}
    </div>
  );
};


// Helper para renderizar ícones dinamicamente
const IconRenderer: React.FC<{ iconName?: string; className?: string }> = ({ iconName, className }) => {
  if (!iconName) return <Eclipse className={className} />;

  const iconMap: Record<string, LucideIcon> = {
    Award, Zap, ThumbsUp, ShieldCheck, Lightbulb, Users, Clock, TrendingUp, Star, Heart,
    MapPin, Phone, Mail, Instagram, Facebook, Linkedin, Twitter, Youtube, Globe, Eclipse,
    ExternalLink, ChevronRight, MessageSquare, Send, Share2, Eye, EyeOff, CircleUserRound, Smartphone, LocateFixed, Clock10, AlertTriangle
  };
  const IconComponent = iconMap[iconName] || Star;
  return <IconComponent className={className} />;
};

// Helper para renderizar HTML de forma segura
const SafeHtmlRenderer: React.FC<{ htmlString?: string; className?: string; style?: React.CSSProperties }> = ({ htmlString, className, style }) => {
  if (!htmlString) return null;
  return <div className={className} dangerouslySetInnerHTML={{ __html: htmlString }} style={style} />;
};

// Helper para renderizar botões de ação com cores personalizadas
const RenderActionButton: React.FC<{
  button?: ActionButton;
  siteData: SiteData;
  defaultText?: string;
  defaultLink?: string;
  additionalClasses?: string;
  children?: React.ReactNode;
  forcedBgColor?: string;
  forcedTextColor?: string;
}> = ({ button, siteData, defaultText = 'Saiba Mais', defaultLink = '#', additionalClasses = '', children, forcedBgColor, forcedTextColor }) => {
  if (!button?.text && !children) return null;

  const text = button?.text || children || defaultText;
  const link = button?.link || defaultLink;

  const bgColor = forcedBgColor || button?.backgroundColor || 'var(--template-accent-color)';
  const textColor = forcedTextColor || button?.textColor || '#FFFFFF';

  return (
    <Link
      href={link}
      className={`inline-flex items-center rounded-md px-6 py-3 text-lg font-semibold shadow-md transition-colors hover:opacity-90 ${additionalClasses}`}
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      {text}
      {button?.text && <ChevronRight className="ml-2 h-5 w-5" />}
    </Link>
  );
};

// Função utilitária para gerar links de navegação
const generateNavigationLinks = (data: SiteData): { label: string; href: string }[] => {
    const links: { label: string; href: string }[] = [];
    if (typeof document === 'undefined') return links;

    const localTempDiv = document.createElement('div');

    const getCleanLabel = (htmlString: string | undefined, fallbackLabel: string): string => {
      if (!htmlString || htmlString.trim() === "" || htmlString.toLowerCase() === 'string') return fallbackLabel;
      localTempDiv.innerHTML = htmlString;
      return (localTempDiv.textContent || localTempDiv.innerText || fallbackLabel).trim();
    };

    if (data.block2_isActive ?? true) links.push({ label: getCleanLabel(data.companyName || data.siteName, 'Início'), href: '#hero' });
    if (data.block3_isActive ?? true) links.push({ label: getCleanLabel(data.block3_title, 'Sobre Nós'), href: '#sobre' });
    if (data.block4_isActive ?? true) links.push({ label: getCleanLabel(data.block4_title, 'Nossos Serviços'), href: '#servicos' });
    if ((data.block_diff_isActive ?? true) && data.block_diff_items && data.block_diff_items.length > 0) links.push({ label: getCleanLabel(data.block_diff_title, 'Diferenciais'), href: '#diferenciais' });
    if ((data.block5_isActive ?? true) && data.block5_testimonials && data.block5_testimonials.length > 0) links.push({ label: getCleanLabel(data.block5_title, 'Depoimentos'), href: '#depoimentos' });
    if ((data.block6_isActive ?? true) && data.block6_clientLogos && data.block6_clientLogos.length > 0) links.push({ label: getCleanLabel(data.block6_title, 'Clientes'), href: '#clientes' });
    if ((data.block7_isActive ?? true) && data.block7_galleryImages && data.block7_galleryImages.length > 0) links.push({ label: getCleanLabel(data.block7_title, 'Galeria'), href: '#galeria' });
    if ((data.block8_isActive ?? true) && data.block8_faqs && data.block8_faqs.length > 0) links.push({ label: getCleanLabel(data.block8_title, 'FAQ'), href: '#faq' });
    if (data.block10_isActive ?? true) links.push({ label: getCleanLabel(data.block10_title, 'Localização'), href: '#local' });
    if (data.block9_isActive ?? true) links.push({ label: 'Fale Conosco', href: '#cta' });
    if (data.block_footer_isActive ?? true) links.push({ label: 'Contato', href: '#contato'});
    return links;
  };


// --- Seções do Template 2 ---
const HeroSection: React.FC<{ data: SiteData }> = ({ data }) => {
  const isActive = data.block2_isActive ?? true;
  if (!isActive) return null;

  const titleHtml = (data.block2_title && data.block2_title.toLowerCase() !== 'string') ? data.block2_title : 'Bem-vindo!';
  const subtitleHtml = (data.block2_subtitle && data.block2_subtitle.toLowerCase() !== 'string') ? data.block2_subtitle : 'Conheça nossos produtos e serviços.';
  const heroImageSrc = isValidImageUrl(data.block2_backgroundImageUrl) ? data.block2_backgroundImageUrl : 'https://placehold.co/1920x1080.png';
  const isPexelsImage = typeof heroImageSrc === 'string' && heroImageSrc.includes('images.pexels.com');
  
  const getGradientStyle = () => {
    if (data.block2_overlayColor) {
      const rgbaMatch = data.block2_overlayColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
      if (rgbaMatch) {
        const [_, r, g, b, a] = rgbaMatch;
        const colorWithOpacity = `rgba(${r}, ${g}, ${b}, ${a})`;
        const colorTransparent = `rgba(${r}, ${g}, ${b}, 0)`;
        return { background: `linear-gradient(to right, ${colorWithOpacity} 30%, ${colorTransparent} 100%)` };
      }
    }
    return { background: `linear-gradient(to right, white, rgba(255, 255, 255, 0.8), transparent)` };
  };
  
  const getContrastingTextColor = () => {
    if (data.block2_overlayColor) {
      const rgbaMatch = data.block2_overlayColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (rgbaMatch) {
        const [_, r, g, b] = rgbaMatch;
        const yiq = ((parseInt(r) * 299) + (parseInt(g) * 587) + (parseInt(b) * 114)) / 1000;
        return yiq >= 128 ? 'text-gray-900' : 'text-white';
      }
    }
    return 'text-gray-900';
  };
  
  const textColorClass = getContrastingTextColor();

  return (
    <ScrollRevealWrapper delay="delay-0" duration="duration-1000" threshold={0.05}>
      <section id="hero" className="relative flex min-h-[70vh] md:min-h-[calc(100vh-80px)] items-center overflow-hidden bg-gray-900 py-20">
        <Image src={heroImageSrc!} alt={data.siteName || 'Imagem de fundo'} fill priority className="object-cover" data-ai-hint={data.block2_backgroundImage_ai_hint || "hero background"} sizes="100vw" unoptimized={isPexelsImage} />
        
        <div className="absolute inset-0" style={getGradientStyle()} />
        
        <div className="container relative z-10 mx-auto px-4">
            <div className="md:w-1/2 lg:w-3/5 text-left">
              <div 
                  className={cn("font-headline font-extrabold text-4xl md:text-5xl lg:text-6xl", textColorClass)} 
              >
                <SafeHtmlRenderer htmlString={titleHtml} className="[&_h1]:text-inherit [&_h1]:font-inherit [&_h2]:text-inherit [&_h2]:font-inherit [&_h3]:font-inherit [&_h4]:font-inherit [&_p]:font-inherit [&_strong]:font-inherit" />
              </div>
              {subtitleHtml && 
                <div 
                    className={cn("mt-6 max-w-xl text-lg", textColorClass === 'text-white' ? 'text-white/90' : 'text-gray-600')}
                >
                  <SafeHtmlRenderer htmlString={subtitleHtml} className="[&_h1]:text-inherit [&_h1]:font-inherit [&_h2]:text-inherit [&_h2]:font-inherit [&_h3]:font-inherit [&_h4]:font-inherit [&_p]:font-inherit [&_strong]:font-inherit" />
                </div>
              }
              <div className="mt-10 flex flex-col items-start justify-start gap-4 sm:flex-row">
                {data.block2_buttons?.map((button, index) => {
                  if (!button.text) return null;

                  const isPrimary = index === 0;
                  const bgColor = button.backgroundColor || (isPrimary ? 'var(--template-accent-color)' : '#FFFFFF');
                  const textColor = button.textColor || (isPrimary ? '#FFFFFF' : '#000000');
                  const borderClass = !isPrimary ? 'border-2 border-gray-400' : ''; 
                  const hoverClasses = isPrimary ? 'hover:opacity-90' : 'hover:bg-gray-100';

                  return (
                    <Link key={index} href={button.link || '#'} className={`rounded-md px-6 py-2.5 text-base font-semibold transition-colors shadow-md ${borderClass} ${hoverClasses}`} style={{ backgroundColor: bgColor, color: textColor }}>
                      {button.text}
                    </Link>
                  );
                })}
              </div>
            </div>
        </div>
      </section>
    </ScrollRevealWrapper>
  );
};

const AboutUsSection: React.FC<{ data: SiteData }> = ({ data }) => {
  const isActive = data.block3_isActive ?? true;
  if (!isActive) return null;
  
  const textHtml = (data.block3_text && data.block3_text.toLowerCase() !== 'string') ? data.block3_text : '<p>Detalhes sobre a empresa aqui.</p>';
  const aboutImageSrc = isValidImageUrl(data.block3_photoUrl) ? data.block3_photoUrl : 'https://placehold.co/800x600.png';
  const isPexelsImage = typeof aboutImageSrc === 'string' && aboutImageSrc.includes('images.pexels.com');

  return (
    <ScrollRevealWrapper>
      <section id="sobre" className="py-20 md:py-28 bg-white overflow-hidden">
        <div className="container mx-auto px-4">
          {/* Mobile view: Stacked layout */}
          <div className="lg:hidden">
            <div className="relative aspect-[4/3] w-full h-full rounded-lg shadow-xl overflow-hidden mb-8">
              <Image
                src={aboutImageSrc!}
                alt={data.block3_title || 'Foto sobre a empresa'}
                fill
                className="object-cover rounded-lg"
                data-ai-hint={data.block3_photo_ai_hint || "company team office"}
                sizes="100vw"
                unoptimized={isPexelsImage}
              />
            </div>
             <div className="rounded-lg p-8 md:p-12 shadow-2xl" style={{ backgroundColor: 'var(--template-primary-color)'}}>
                <h2 className="font-headline text-4xl font-bold md:text-5xl text-white">
                  {(data.block3_title && data.block3_title.toLowerCase() !== 'string') ? data.block3_title : 'Sobre Nós'}
                </h2>
                <div className="mt-6 flex-grow text-white/90">
                  <SafeHtmlRenderer htmlString={textHtml} />
                </div>
                {data.block3_actionButton?.text && (
                  <div className="mt-8 text-center sm:text-left">
                    <RenderActionButton button={data.block3_actionButton} siteData={data} />
                  </div>
                )}
              </div>
          </div>
          
          {/* Desktop view: Overlapping layout */}
          <div className="hidden lg:relative lg:flex lg:items-center">
            <div className="lg:absolute lg:right-0 lg:top-1/2 lg:-translate-y-1/2 lg:w-1/2 h-full min-h-[300px]">
               <div className="relative aspect-[4/3] w-full h-full rounded-lg shadow-xl overflow-hidden">
                  <Image
                    src={aboutImageSrc!}
                    alt={data.block3_title || 'Foto sobre a empresa'}
                    fill
                    className="object-cover rounded-lg"
                    data-ai-hint={data.block3_photo_ai_hint || "company team office"}
                    sizes="50vw"
                    unoptimized={isPexelsImage}
                  />
               </div>
            </div>
            <div className="lg:w-7/12 lg:pr-16">
              <div
                className="relative z-10 rounded-lg p-8 md:p-12 shadow-2xl h-full flex flex-col w-full lg:my-12"
                style={{ backgroundColor: 'var(--template-primary-color)'}}
              >
                <h2 className="font-headline text-4xl font-bold md:text-5xl text-white">
                  {(data.block3_title && data.block3_title.toLowerCase() !== 'string') ? data.block3_title : 'Sobre Nós'}
                </h2>
                <div className="mt-6 flex-grow text-white/90">
                  <SafeHtmlRenderer htmlString={textHtml} />
                </div>
                {data.block3_actionButton?.text && (
                  <div className="mt-8 text-center sm:text-left">
                    <RenderActionButton button={data.block3_actionButton} siteData={data} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </ScrollRevealWrapper>
  );
};
  
const ServicesSection: React.FC<{ data: SiteData }> = ({ data }) => {
  const isActive = data.block4_isActive ?? true;
  if (!isActive) return null;
  
  const textHtml = (data.block4_text && data.block4_text.toLowerCase() !== 'string') ? data.block4_text : '<p>Informações sobre nossos serviços.</p>';
  const servicesImageSrc = isValidImageUrl(data.block4_photoUrl) ? data.block4_photoUrl : 'https://placehold.co/800x600.png';
  const isPexelsImage = typeof servicesImageSrc === 'string' && servicesImageSrc.includes('images.pexels.com');

  return (
    <ScrollRevealWrapper>
      <section id="servicos" className="py-20 md:py-28 bg-white overflow-hidden">
        <div className="container mx-auto px-4">
          {/* Mobile view: Stacked layout */}
          <div className="lg:hidden">
            <div className="relative aspect-[4/3] w-full h-full rounded-lg shadow-xl overflow-hidden mb-8">
              <Image
                src={servicesImageSrc!}
                alt={data.block4_title || 'Foto dos serviços'}
                fill
                className="object-cover rounded-lg"
                data-ai-hint={data.block4_photo_ai_hint || "product service item"}
                sizes="100vw"
                unoptimized={isPexelsImage}
              />
            </div>
             <div className="rounded-lg p-8 md:p-12 shadow-2xl" style={{ backgroundColor: 'var(--template-secondary-color)'}}>
                <h2 className="font-headline text-4xl font-bold md:text-5xl text-white">
                  {(data.block4_title && data.block4_title.toLowerCase() !== 'string') ? data.block4_title : 'Nossos Serviços'}
                </h2>
                <div className="mt-6 flex-grow text-white/90">
                  <SafeHtmlRenderer htmlString={textHtml} />
                </div>
                {data.block4_actionButton?.text && (
                  <div className="mt-8 text-center sm:text-left">
                    <RenderActionButton button={data.block4_actionButton} siteData={data} />
                  </div>
                )}
              </div>
          </div>
          
          {/* Desktop view: Overlapping layout */}
          <div className="hidden lg:relative lg:flex lg:items-center">
             <div className="lg:absolute lg:left-0 lg:top-1/2 lg:-translate-y-1/2 lg:w-1/2 h-full min-h-[300px]">
              <div className="relative aspect-[4/3] w-full h-full rounded-lg shadow-xl overflow-hidden">
                <Image
                  src={servicesImageSrc!}
                  alt={data.block4_title || 'Foto dos serviços'}
                  fill
                  className="object-cover rounded-lg"
                  data-ai-hint={data.block4_photo_ai_hint || "product service item"}
                  sizes="50vw"
                  unoptimized={isPexelsImage}
                />
              </div>
            </div>
            <div className="lg:w-7/12 lg:pl-16 lg:ml-auto">
              <div
                className="relative z-10 rounded-lg p-8 md:p-12 shadow-2xl h-full flex flex-col w-full lg:my-12"
                style={{ backgroundColor: 'var(--template-secondary-color)'}}
              >
                <h2 className="font-headline text-4xl font-bold md:text-5xl text-white">
                  {(data.block4_title && data.block4_title.toLowerCase() !== 'string') ? data.block4_title : 'Nossos Serviços'}
                </h2>
                <div className="mt-6 flex-grow text-white/90">
                  <SafeHtmlRenderer htmlString={textHtml} />
                </div>
                {data.block4_actionButton?.text && (
                  <div className="mt-8 text-center sm:text-left">
                    <RenderActionButton button={data.block4_actionButton} siteData={data} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </ScrollRevealWrapper>
  );
};

const DifferentiatorsSection: React.FC<{ data: SiteData }> = ({ data }) => {
    const isActive = data.block_diff_isActive ?? true;
    const items = data.block_diff_items || [];
    if (!isActive || items.length === 0) return null;
  
    return (
      <ScrollRevealWrapper>
        <section id="diferenciais" className="py-20 md:py-28 bg-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="font-headline text-4xl font-bold md:text-5xl mb-4" style={{ color: 'var(--template-primary-color)' }}>
              {data.block_diff_title || 'Nossos Diferenciais'}
            </h2>
            {data.block_diff_subtitle && <p className="text-xl text-gray-600 mb-16 max-w-2xl mx-auto">{data.block_diff_subtitle}</p>}
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {items.map(item => (
                <div 
                  key={item.id} 
                  className="flex flex-col items-center text-center p-6 transition-transform duration-300 hover:-translate-y-2 border border-solid rounded-[20px]"
                  style={{ borderColor: 'var(--template-accent-color)' }}
                >
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full" style={{ backgroundColor: 'var(--template-accent-color)' }}>
                    <IconRenderer iconName={item.icon} className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="font-headline text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
            {data.block_diff_actionButton?.text && <div className="mt-12 text-center"><RenderActionButton button={data.block_diff_actionButton} siteData={data} /></div>}
          </div>
        </section>
      </ScrollRevealWrapper>
    );
  };

  const TestimonialsSection: React.FC<{ data: SiteData }> = ({ data }) => {
    const isActive = data.block5_isActive ?? true;
    const testimonials = data.block5_testimonials || [];
    if (!isActive || testimonials.length === 0) return null;
  
    return (
      <ScrollRevealWrapper>
        <section id="depoimentos" className="py-20 md:py-28" style={{ backgroundColor: 'var(--template-secondary-color)' }}>
          <div className="container mx-auto px-4">
            <h2 className="font-headline text-4xl font-bold text-center text-white md:text-5xl mb-4">
              {data.block5_title || 'O Que Nossos Clientes Dizem'}
            </h2>
            {data.block5_subtitle && <p className="text-xl text-center text-white/80 mb-16 max-w-2xl mx-auto">{data.block5_subtitle}</p>}
            <Carousel opts={{ align: "start", loop: testimonials.length > 2 }} className="w-full">
              <CarouselContent className="-ml-4">
                {testimonials.map((testimonial) => {
                  const testimonialImageSrc = isValidImageUrl(testimonial.photoUrl) ? testimonial.photoUrl : null;
                  const isPexelsImage = typeof testimonialImageSrc === 'string' && testimonialImageSrc.includes('images.pexels.com');
                  return (
                    <CarouselItem key={testimonial.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                      <div className="p-1 h-full">
                        <div className="flex h-full flex-col justify-center rounded-lg bg-white p-8 text-center shadow-lg">
                          <p className="flex-grow text-gray-700 text-lg italic mb-6">&quot;{testimonial.text}&quot;</p>
                          <div className="flex items-center justify-center">
                            <div className="mr-4 h-14 w-14 overflow-hidden rounded-full border-2" style={{ borderColor: 'var(--template-accent-color)' }}>
                              {testimonialImageSrc ? <Image src={testimonialImageSrc} alt={`Foto de ${testimonial.name}`} width={100} height={100} className="h-full w-full object-cover" unoptimized={isPexelsImage} /> : <CircleUserRound className="h-full w-full text-gray-400" />}
                            </div>
                            <div>
                              <p className="font-headline text-lg font-semibold">{testimonial.name}</p>
                              <p className="text-sm text-gray-500">{testimonial.title}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <CarouselPrevious className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-8" />
              <CarouselNext className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-8" />
            </Carousel>
            {data.block5_actionButton?.text && <div className="mt-12 text-center"><RenderActionButton button={data.block5_actionButton} siteData={data} /></div>}
          </div>
        </section>
      </ScrollRevealWrapper>
    );
  };
  
const ClientLogosSection: React.FC<{ data: SiteData }> = ({ data }) => {
    const isActive = data.block6_isActive ?? true;
    const logos = data.block6_clientLogos || [];

    const [api, setApi] = React.useState<CarouselApi>()
    const plugin = React.useRef(
        Autoplay({ delay: 2500, stopOnInteraction: true, stopOnMouseEnter: true })
    );

    React.useEffect(() => {
        if (!api) return;
        api.on("reInit", () => {});
      }, [api]);

    if (!isActive || logos.length === 0) {
      return null;
    }

    return (
      <ScrollRevealWrapper>
        <section id="clientes" className="py-12 md:py-20 bg-white">
            <div className="container mx-auto px-4">
                <h2 className="font-headline text-3xl font-bold text-center text-gray-800 md:text-4xl mb-10" style={{ color: 'var(--template-primary-color)' }}>
                    {data.block6_title || 'Clientes que Confiam em Nós'}
                </h2>
                <div className="relative">
                    <Carousel setApi={setApi} plugins={[plugin.current]} opts={{ align: "start", loop: logos.length > 5 }} className="w-full">
                        <CarouselContent className="-ml-2 md:-ml-4">
                            {logos.map((logo) => {
                                const placeholderText = encodeURIComponent(logo.altText || "Cliente").replace(/%20/g, '+');
                                const clientLogoSrc = isValidImageUrl(logo.logoUrl) 
                                    ? logo.logoUrl 
                                    : `https://placehold.co/150x150.png?text=${placeholderText}`;
                                
                                const isPexelsImage = typeof clientLogoSrc === 'string' && clientLogoSrc.includes('images.pexels.com');
                                return (
                                    <CarouselItem key={logo.id} className="pl-2 md:pl-4 basis-1/2 sm:basis-1/3 md:basis-1/4 lg:basis-1/5 flex items-center justify-center">
                                        <div className="flex-shrink-0 w-28 h-28 aspect-square relative flex items-center justify-center filter grayscale transition hover:grayscale-0">
                                            <Image src={clientLogoSrc!} alt={logo.altText || `Logo cliente`} fill sizes="(max-width: 768px) 20vw, 10vw" className="object-contain p-1" data-ai-hint="client brand" unoptimized={isPexelsImage} />
                                        </div>
                                    </CarouselItem>
                                )
                            })}
                        </CarouselContent>
                    </Carousel>
                </div>
            </div>
        </section>
      </ScrollRevealWrapper>
    );
};

const GallerySection: React.FC<{ data: SiteData }> = ({ data }) => {
    const isActive = data.block7_isActive ?? true;
    const images = data.block7_galleryImages || [];

    if (!isActive || images.length === 0) {
      return null;
    }

    return (
      <ScrollRevealWrapper>
        <section id="galeria" className="py-20 md:py-28" style={{ backgroundColor: 'var(--template-secondary-color)' }}>
            <div className="container mx-auto px-4">
                <h2 className="font-headline text-4xl font-bold text-center text-white md:text-5xl mb-4">
                    {data.block7_title || 'Nosso Trabalho'}
                </h2>
                {data.block7_subtitle && (
                      <p className="text-xl text-center text-white/80 mb-16 max-w-2xl mx-auto">
                        {data.block7_subtitle}
                      </p>
                )}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {images.map((imageItem) => {
                        const galleryImageSrc = isValidImageUrl(imageItem.imageUrl) ? imageItem.imageUrl : 'https://placehold.co/600x400.png';
                        const isPexelsImage = typeof galleryImageSrc === 'string' && galleryImageSrc.includes('images.pexels.com');
                        const hint = imageItem.imageHint || imageItem.altText?.trim().split(/\s+/).slice(0, 2).join(' ') || 'portfolio work';

                        return (
                            <div key={imageItem.id} className="group relative aspect-video overflow-hidden rounded-lg shadow-lg">
                                <Image
                                    src={galleryImageSrc!}
                                    alt={imageItem.altText || `Imagem da galeria ${imageItem.id}`}
                                    fill
                                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                                    data-ai-hint={hint}
                                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                    unoptimized={isPexelsImage}
                                />
                                {imageItem.altText && (
                                    <div className="absolute inset-x-0 bottom-0 bg-black/60 p-3 text-center text-sm text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                                        {imageItem.altText}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
                {data.block7_actionButton?.text && (
                  <div className="mt-12 text-center">
                    <RenderActionButton 
                        button={data.block7_actionButton} 
                        siteData={data} 
                    />
                  </div>
                )}
            </div>
        </section>
      </ScrollRevealWrapper>
    );
};

const FAQSection: React.FC<{ data: SiteData }> = ({ data }) => {
  const isActive = data.block8_isActive ?? true;
  const faqs = data.block8_faqs || [];
  if (!isActive || faqs.length === 0) return null;

  return (
    <ScrollRevealWrapper>
      <section id="faq" className="py-20 md:py-28 bg-slate-100">
        <div className="container mx-auto max-w-3xl px-4">
          <h2 className="font-headline text-4xl font-bold text-center text-gray-800 md:text-5xl mb-4">
            {data.block8_title || 'Perguntas Frequentes'}
          </h2>
          {data.block8_subtitle && (
            <p className="text-xl text-center text-gray-600 mb-16 max-w-2xl mx-auto">
              {data.block8_subtitle}
            </p>
          )}
          <Accordion type="single" collapsible className="w-full space-y-3">
            {faqs.map((faqItem) => (
              <AccordionItem value={faqItem.id} key={faqItem.id} className="rounded-lg border bg-white shadow-sm data-[state=open]:shadow-md">
                <AccordionTrigger className="px-6 py-4 text-left font-semibold text-lg text-gray-800 hover:no-underline">
                  {faqItem.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 pt-0">
                  <SafeHtmlRenderer htmlString={faqItem.answer} className="prose text-gray-600" />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </ScrollRevealWrapper>
  );
};

const CTASection: React.FC<{ data: SiteData }> = ({ data }) => {
  const isActive = data.block9_isActive ?? true;
  if (!isActive) return null;

  const ctaImageSrc = isValidImageUrl(data.block9_backgroundImageUrl) ? data.block9_backgroundImageUrl : 'https://placehold.co/1920x720.png';
  const isPexelsImage = typeof ctaImageSrc === 'string' && ctaImageSrc.includes('images.pexels.com');

  return (
    <ScrollRevealWrapper>
      <section
        id="cta"
        className="relative overflow-hidden bg-gray-800 py-24 text-white"
      >
        <Image
          src={ctaImageSrc!}
          alt={data.block9_impactPhrase || 'Imagem de fundo do CTA'}
          fill
          className="object-cover"
          data-ai-hint={data.block9_backgroundImage_ai_hint || "call to action"}
          sizes="100vw"
          unoptimized={isPexelsImage}
        />
        <div
            className="absolute inset-0"
            style={{ backgroundColor: data.block9_overlayColor || 'rgba(0,0,0,0.5)' }}
        />
        <div className="container relative z-10 mx-auto px-4 text-center">
          <h2 className="font-headline text-4xl font-bold md:text-5xl lg:text-6xl">
            {(data.block9_impactPhrase && data.block9_impactPhrase.toLowerCase() !== 'string') ? data.block9_impactPhrase : 'Pronto para Começar?'}
          </h2>
          {data.block9_actionButton?.text && (
            <div className="mt-8">
              <RenderActionButton
                button={data.block9_actionButton}
                siteData={data}
                additionalClasses="px-10 py-4 text-xl"
                forcedBgColor="white"
                forcedTextColor="var(--template-primary-color)"
              />
            </div>
          )}
        </div>
      </section>
    </ScrollRevealWrapper>
  );
};

const LocationContactSection: React.FC<{ data: SiteData }> = ({ data }) => {
  const isActive = data.block10_isActive ?? true;
  if (!isActive) return null;

  const mapsApiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
  
  const addressFromFields = [
      data.address_street,
      data.address_number,
      data.address_neighborhood,
      data.address_city,
      data.address_state,
  ].filter(Boolean).join(', ');

  const addressString = (addressFromFields || data.address || '').replace(/CEP\s*\d{5}-?\d{3}/i, '').trim();
  const mapQuery = addressString || data.companyName || '';
  const mapSrc = `https://www.google.com/maps/embed/v1/place?key=${mapsApiKey}&q=${encodeURIComponent(mapQuery)}`;
  
  const showColumn1 = (data.block10_column1_isActive ?? true) && data.block10_column1_text && data.block10_column1_text.trim() !== '';
  const showColumn2 = (data.block10_column2_map_isActive ?? true) && mapQuery && mapsApiKey;
  const showColumn3 = (data.block10_column3_form_isActive ?? true) && data.contactEmail;
  
  let visibleColumnsCount = 0;
  if (showColumn1) visibleColumnsCount++;
  if (showColumn2) visibleColumnsCount++;
  if (showColumn3) visibleColumnsCount++;
  
  const gridColsClass = cn(
    "md:grid-cols-1", 
    visibleColumnsCount === 2 && "md:grid-cols-2",
    visibleColumnsCount >= 3 && "lg:grid-cols-3"
  );

  return (
    <ScrollRevealWrapper>
      <section id="local" className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="font-headline text-4xl font-bold text-center md:text-5xl mb-12" style={{ color: 'var(--template-primary-color)' }}>
            {(data.block10_title && data.block10_title.toLowerCase() !== 'string') ? data.block10_title : 'Visite-nos ou Entre em Contato'}
          </h2>
          <div className={cn("grid grid-cols-1 gap-8 items-center", gridColsClass)}>
            {showColumn1 && (
              <div className="md:col-span-1 relative min-h-[300px] flex items-center justify-center lg:my-12">
                  <div className="absolute left-0 top-0 h-full w-1/2 rounded-lg" style={{ backgroundColor: 'var(--template-primary-color)' }} />
                  <div className="relative w-[90%] bg-white p-6 rounded-lg border-2 my-5" style={{ borderColor: 'var(--template-secondary-color)' }}>
                      <SafeHtmlRenderer htmlString={data.block10_column1_text} className="prose prose-sm text-gray-700" />
                  </div>
              </div>
            )}
            {showColumn2 && (
              <div className="md:col-span-1 h-[450px] rounded-lg shadow-xl overflow-hidden border-2" style={{ borderColor: 'var(--template-secondary-color)' }}>
                  <iframe
                      width="100%"
                      height="100%"
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                      src={mapSrc}
                      className="rounded-lg"
                      title={`Mapa para ${data.companyName || 'nossa localização'}`}
                  ></iframe>
              </div>
            )}
            {showColumn3 && (
                 <div className="md:col-span-1 p-8 rounded-lg shadow-2xl" style={{ backgroundColor: 'var(--template-primary-color)' }}>
                    <MiniSiteContactForm
                        contactToEmail={data.contactEmail!}
                        title={(data.block10_column3_formTitle && data.block10_column3_formTitle.toLowerCase() !== 'string') ? data.block10_column3_formTitle : "Fale Conosco"}
                        titleClassName="text-white"
                    />
                 </div>
            )}
          </div>
        </div>
      </section>
    </ScrollRevealWrapper>
  );
};


// --- Report Abuse Modal and Form Component ---
const ReportAbuseModal: React.FC<{ siteData: SiteData }> = ({ siteData: modalSiteDataProp }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isSubmittingReport, setIsSubmittingReport] = useState(false);
    const { register, handleSubmit, formState: { errors }, reset } = useForm<ReportAbuseFormData>({ resolver: zodResolver(ReportAbuseFormDataSchema), defaultValues: { reportedSiteSubdomain: modalSiteDataProp.subdomain, reportedSiteCustomDomain: modalSiteDataProp.customDomain || '' } });
    const onReportSubmit: SubmitHandler<ReportAbuseFormData> = async (formData) => {
        setIsSubmittingReport(true);
        try {
            const result = await submitReportAbuse(formData);
            if (result.success) { toast({ title: "Denúncia Enviada", description: result.message }); reset(); setIsOpen(false); }
            else { toast({ title: "Erro ao Enviar Denúncia", description: result.message, variant: "destructive" }); }
        } catch (error) { toast({ title: "Erro Inesperado", description: "Ocorreu um problema.", variant: "destructive" }); }
        finally { setIsSubmittingReport(false); }
    };
    useEffect(() => { if (!isOpen) reset({ reportedSiteSubdomain: modalSiteDataProp.subdomain, reportedSiteCustomDomain: modalSiteDataProp.customDomain || '', reporterName: '', reporterEmail: '', reporterPhone: '', reportMessage: '' }); }, [isOpen, reset, modalSiteDataProp.subdomain, modalSiteDataProp.customDomain]);
    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild><button className="mt-4 text-sm font-semibold text-slate-500 hover:text-template-primary hover:underline flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Denunciar conteúdo</button></DialogTrigger>
          <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle className="font-headline">Denunciar Conteúdo</DialogTitle><DialogDescription>Denuncie o site {modalSiteDataProp.customDomain || `${modalSiteDataProp.subdomain}.website.app.br`}.</DialogDescription></DialogHeader>
            <form onSubmit={handleSubmit(onReportSubmit)} className="space-y-4">
                <div className="absolute opacity-0 h-0 w-0">
                    <label htmlFor="report-honeypot">Não preencha este campo</label>
                    <input id="report-honeypot" tabIndex={-1} autoComplete="off" {...register("honeypot")} />
                </div>
                <div><Label htmlFor="reporterName">Seu Nome</Label><Input id="reporterName" {...register("reporterName")} disabled={isSubmittingReport} />{errors.reporterName && <p className="text-xs text-destructive mt-1">{errors.reporterName.message}</p>}</div>
                <div><Label htmlFor="reporterEmail">Seu E-mail</Label><Input id="reporterEmail" type="email" {...register("reporterEmail")} disabled={isSubmittingReport} />{errors.reporterEmail && <p className="text-xs text-destructive mt-1">{errors.reporterEmail.message}</p>}</div>
                <div><Label htmlFor="reportMessage">Mensagem</Label><Textarea id="reportMessage" rows={5} {...register("reportMessage")} disabled={isSubmittingReport} />{errors.reportMessage && <p className="text-xs text-destructive mt-1">{errors.reportMessage.message}</p>}</div>
                <DialogFooter className="mt-6"><DialogClose asChild><Button type="button" variant="outline" disabled={isSubmittingReport}>Cancelar</Button></DialogClose><Button type="submit" disabled={isSubmittingReport} style={{ backgroundColor: 'var(--template-primary-color)' }}>{isSubmittingReport ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Enviar Denúncia</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
    );
};

const FooterSection: React.FC<{ data: SiteData }> = ({ data }) => {
  const isActive = data.block_footer_isActive ?? true;
  if (!isActive) return null;
  
  const [sitemapLinks, setSitemapLinks] = useState<{ label: string; href: string }[]>([]);

  useEffect(() => {
    setSitemapLinks(generateNavigationLinks(data));
  }, [data]);


  const socialIconMap: Record<string, LucideIcon> = {
    instagram: Instagram, facebook: Facebook, linkedin: Linkedin,
    x: Twitter, youtube: Youtube, tiktok: MessageSquare, other: Globe,
  };

  const buildSocialLink = (link: SiteData['socialLinks'][0]) => {
    if (!link) return '#';
    if (link.type === 'other') return link.urlSuffix || '#';
    const prefixes: Record<string, string> = {
        instagram: 'https://instagram.com/', facebook: 'https://facebook.com/',
        linkedin: 'https://linkedin.com/company/', x: 'https://x.com/',
        youtube: 'https://youtube.com/', tiktok: 'https://tiktok.com/@',
    };
    return `${prefixes[link.type] || ''}${link.urlSuffix || ''}`;
  };

  const rawFooterLogoUrl = data.block_footer_logoUrl || data.companyLogoUrl;
  const footerLogoSrc = isValidImageUrl(rawFooterLogoUrl) ? rawFooterLogoUrl : null;
  const isPexelsImageForLogo = footerLogoSrc?.includes('images.pexels.com');

  const currentSiteUrl = data.customDomain ? `https://${data.customDomain}` : `https://${data.subdomain}.${process.env.NEXT_PUBLIC_ROOT_DOMAIN || 'website.app.br'}`;

  const hasAddressDetails = data.address || data.address_street || data.address_city || data.address_zipCode;
  const showLogoInfoCol = true; 
  const showContactDetailsCol = (data.block_footer_showAddress && hasAddressDetails) || (data.block_footer_showPhone && data.phone) || (data.block_footer_showWhatsapp && data.whatsapp) || (data.block_footer_showEmail && data.contactEmail);
  const showSitemapCol = (data.block_footer_showSitemap ?? true) && sitemapLinks.length > 0;
  const showContactFormCol = (data.block_footer_showContactForm ?? true) && data.contactEmail;

  let visibleFooterColumns = 0;
  if (showLogoInfoCol) visibleFooterColumns++;
  if (showContactDetailsCol) visibleFooterColumns++;
  if (showSitemapCol) visibleFooterColumns++;
  if (showContactFormCol) visibleFooterColumns++;
  
  const footerGridLayoutClasses = cn(
    "grid-cols-1 gap-10 md:grid-cols-2", 
    visibleFooterColumns >= 4 && "lg:grid-cols-4",
    visibleFooterColumns === 3 && "lg:grid-cols-3",
    visibleFooterColumns === 2 && "lg:grid-cols-2",
    visibleFooterColumns < 2 && "lg:grid-cols-1"
  );

  return (
    <footer id="contato" className="bg-gray-800 text-gray-300 py-12 md:py-16">
      <div className="container mx-auto px-4">
        <div className={cn("grid", footerGridLayoutClasses)}>
          {showLogoInfoCol && (
            <div>
              {footerLogoSrc ? (
                <Link href="#hero" className="mb-4 inline-block"><Image src={footerLogoSrc} alt={`Logo de ${data.siteName || 'Empresa'}`} width={180} height={60} className="h-auto max-h-[60px] object-contain" unoptimized={isPexelsImageForLogo} /></Link>
              ) : (
                <Link href="#hero" className="mb-4 inline-block font-headline text-xl font-bold text-white">{(data.siteName && data.siteName.toLowerCase() !== 'string') ? data.siteName : (data.companyName && data.companyName.toLowerCase() !== 'string' ? data.companyName : "Meu Site Incrível")}</Link>
              )}
              {(data.block_footer_additionalText && data.block_footer_additionalText.toLowerCase() !== 'string') && (<SafeHtmlRenderer htmlString={data.block_footer_additionalText} className="prose-sm text-gray-400" />)}
              {data.socialLinks && data.socialLinks.length > 0 && (data.block_footer_showSocialLinks ?? true) && (
                <div className="mt-4 flex space-x-3">
                  {data.socialLinks.map(link => {
                    if (!link || !link.urlSuffix) return null;
                    const SocialIcon = socialIconMap[link.type] || Globe;
                    const fullUrl = buildSocialLink(link);
                    return (<a key={link.id} href={fullUrl} target="_blank" rel="noopener noreferrer" aria-label={`Link para ${data.companyName || data.siteName} no ${link.type}`} className="text-gray-400 hover:text-white transition-colors"><SocialIcon className="h-5 w-5" /></a>);
                  })}
                </div>
              )}
            </div>
          )}

          {showContactDetailsCol && (
            <div>
              <h4 className="font-headline text-lg font-semibold text-white mb-4">Contato</h4>
              <ul className="space-y-3 text-sm">
                {(data.block_footer_showAddress ?? true) && hasAddressDetails && (<li className="flex items-start"><MapPin className="mr-3 mt-1 h-4 w-4 shrink-0 text-template-primary" /><span>
  {(data.address_street || data.address_city) ? (
    <React.Fragment>
      {data.address_street}{data.address_number ? `, ${data.address_number}` : ''}
      {data.address_complement ? `, ${data.address_complement}` : ''}
      <br />
      {data.address_neighborhood ? `${data.address_neighborhood}, ` : ''}
      {data.address_city || ''} - {data.address_state || ''}
      <br />
      CEP: {data.address_zipCode || ''}
    </React.Fragment>
  ) : (
    data.address
  )}
</span></li>)}
                {(data.block_footer_showPhone ?? true) && data.phone && (<li className="flex items-center"><Phone className="mr-3 h-4 w-4 shrink-0 text-template-primary" /><a href={`tel:${(data.phoneDDI || '').replace(/\D/g, '')}${(data.phone || '').replace(/\D/g, '')}`} className="hover:text-white">{data.phoneDDI || ''} {data.phone}</a></li>)}
                {(data.block_footer_showWhatsapp ?? true) && data.whatsapp && (<li className="flex items-center"><MessageCircle className="mr-3 h-4 w-4 shrink-0 text-template-primary" /><a href={`https://wa.me/${(data.whatsappDDI || '').replace(/\D/g, '')}${(data.whatsapp || '').replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="hover:text-white">{data.whatsappDDI || ''} {data.whatsapp} (WhatsApp)</a></li>)}
                {(data.block_footer_showEmail ?? true) && data.contactEmail && (<li className="flex items-center"><Mail className="mr-3 h-4 w-4 shrink-0 text-template-primary" /><a href={`mailto:${data.contactEmail}`} className="hover:text-white">{data.contactEmail}</a></li>)}
              </ul>
            </div>
          )}

          {showSitemapCol && (
            <div>
              <h4 className="font-headline text-lg font-semibold text-white mb-4">Navegue</h4>
              <ul className="space-y-2 text-sm">
                {sitemapLinks.map((link) => (<li key={link.href}><Link href={link.href} className="hover:text-white">{link.label}</Link></li>))}
              </ul>
            </div>
          )}

          {showContactFormCol && (
            <div className={cn("md:col-span-2", visibleFooterColumns === 4 && "lg:col-span-1", visibleFooterColumns === 3 && "lg:col-span-1", visibleFooterColumns < 3 && "md:col-span-2")}>
              <MiniSiteContactForm contactToEmail={data.contactEmail!} title="Fale Conosco" titleClassName="text-white" />
            </div>
          )}
        </div>
        <div className="mt-10 border-t border-gray-700 pt-8 text-center text-sm text-gray-400">
          <p className="mb-2">&copy; {new Date().getFullYear()} {data.siteName || "Seu Site"}. Todos os direitos reservados.</p>
          <div className="mt-1">{data.privacyPolicyContent && (<Dialog><DialogTrigger asChild><button className="text-sm text-gray-400 hover:text-white hover:underline cursor-pointer">Política de Privacidade</button></DialogTrigger><DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col p-0 bg-white text-black"><DialogHeader className="p-6 pb-4 border-b"><DialogTitle className="text-2xl font-headline">Política de Privacidade</DialogTitle><DialogDescription>Termos para {data.siteName || "este site"}.</DialogDescription></DialogHeader><div className="relative flex-grow min-h-0 overflow-y-auto p-6"><SafeHtmlRenderer htmlString={data.privacyPolicyContent} className="prose prose-sm" /></div><DialogFooter className="p-6 pt-4 border-t"><DialogClose asChild><Button type="button" variant="outline">Fechar</Button></DialogClose></DialogFooter></DialogContent></Dialog>)}</div>
          <div className="flex justify-center mt-4"><ReportAbuseModal siteData={data} /></div>
          <p className="mt-3 text-xs text-gray-500">Desenvolvido com <Link href="https://www.website.app.br" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{color: 'var(--template-accent-color)'}}>WebSite App</Link></p>
           <p className="mt-1 text-xs text-slate-500">
            O conteúdo deste site (<Link href={currentSiteUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">{currentSiteUrl.replace(/^https?:\/\//, '')}</Link>) está sob responsabilidade legal de {data.companyName || data.siteName}.
            Contato: <a href={`mailto:${data.contactEmail}`} className="hover:underline">{data.contactEmail}</a>.
          </p>
        </div>
      </div>
    </footer>
  );
};


// Componente principal que renderiza o Template 2
export function Template2Renderer({ data }: { data: SiteData }) {
  const [navLinks, setNavLinks] = useState<{ label: string; href: string }[]>([]);

  useEffect(() => { setNavLinks(generateNavigationLinks(data)); }, [data]);

  const rawCompanyLogoUrl = data.companyLogoUrl;
  let displayLogoAsImage = false;
  let logoSrcForImageComponent = isValidImageUrl(rawCompanyLogoUrl) ? rawCompanyLogoUrl! : 'https://placehold.co/150x60.png?text=Logo';
  if (isValidImageUrl(rawCompanyLogoUrl)) {
    displayLogoAsImage = true;
  }
  const isPexelsImageForLogo = logoSrcForImageComponent.includes('images.pexels.com');

  const sections = React.useMemo(() => [
    { key: 'hero', component: HeroSection, order: data.block2_order, isActive: data.block2_isActive ?? true },
    { key: 'about', component: AboutUsSection, order: data.block3_order, isActive: data.block3_isActive ?? true },
    { key: 'services', component: ServicesSection, order: data.block4_order, isActive: data.block4_isActive ?? true },
    { key: 'differentiators', component: DifferentiatorsSection, order: data.block_diff_order, isActive: data.block_diff_isActive ?? true },
    { key: 'testimonials', component: TestimonialsSection, order: data.block5_order, isActive: data.block5_isActive ?? true },
    { key: 'clientLogos', component: ClientLogosSection, order: data.block6_order, isActive: data.block6_isActive ?? true },
    { key: 'gallery', component: GallerySection, order: data.block7_order, isActive: data.block7_isActive ?? true },
    { key: 'faq', component: FAQSection, order: data.block8_order, isActive: data.block8_isActive ?? true },
    { key: 'locationContact', component: LocationContactSection, order: data.block10_order, isActive: data.block10_isActive ?? true },
    { key: 'cta', component: CTASection, order: data.block9_order, isActive: data.block9_isActive ?? true },
  ], [data]);

  const sortedAndActiveSections = React.useMemo(() => sections.filter(section => section.isActive).sort((a, b) => (a.order ?? 99) - (b.order ?? 99)), [sections]);

  const showWhatsappButton = (data.block_footer_showWhatsappButton ?? true) && data.whatsapp;
  const whatsappLink = `https://wa.me/${(data.whatsappDDI || '').replace(/\D/g, '')}${(data.whatsapp || '').replace(/\D/g, '')}`;

  return (
    <>
      {data.googleAnalyticsId && ( <><Script strategy="afterInteractive" src={`https://www.googletagmanager.com/gtag/js?id=${data.googleAnalyticsId}`} /><Script id="google-analytics-and-ads" strategy="afterInteractive" dangerouslySetInnerHTML={{ __html: `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${data.googleAnalyticsId}'); ${data.googleAdsId ? `gtag('config', '${data.googleAdsId}');` : ''}`}} /></>)}
      {data.facebookPixelId && (<Script id="fb-pixel" strategy="afterInteractive" dangerouslySetInnerHTML={{ __html: `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script','https://connect.facebook.net/en_US/fbevents.js'); fbq('init', '${data.facebookPixelId}'); fbq('track', 'PageView');`}} />)}
      <div className="font-body bg-white text-gray-800 antialiased">
        <style jsx global>{`
          :root {
            --template-primary-color: ${data.primaryColor || '#7c3aed'};
            --template-secondary-color: ${data.secondaryColor || '#f3e8ff'};
            --template-accent-color: ${data.accentColor || '#a855f7'};
          }
           .prose-lg p { color: inherit; }
           .prose-invert p, .prose-invert ul, .prose-invert ol, .prose-invert blockquote { color: #fff !important; }
           .prose-invert h1, .prose-invert h2, .prose-invert h3, .prose-invert h4 { color: white; }
           .prose-invert strong { color: white; }
           .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 { color: inherit }
        `}</style>
        
        <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container mx-auto flex items-center justify-between px-4">
            <div className="flex-shrink-0">
              {displayLogoAsImage ? (
                <Link href="#hero" className="flex items-center">
                  <Image
                    src={logoSrcForImageComponent}
                    alt={`Logo de ${data.siteName || 'Empresa'}`}
                    width={150}
                    height={100}
                    className="h-auto max-h-[60px] w-auto object-contain"
                    data-ai-hint="company logo"
                    unoptimized={isPexelsImageForLogo}
                  />
                </Link>
              ) : (
                <Link href="#hero" className="font-headline text-2xl font-bold" style={{color: 'var(--template-primary-color)'}}>
                  {(data.siteName && data.siteName.toLowerCase() !== 'string') ? data.siteName : (data.companyName && data.companyName.toLowerCase() !== 'string' ? data.companyName : "Meu Site Incrível")}
                </Link>
              )}
            </div>

            <div className="flex items-center">
              <div className="hidden sm:flex items-center gap-1 md:gap-2">
                {data.contactEmail && (
                  <a href={`mailto:${data.contactEmail}`} aria-label="Email de contato" className="flex items-center text-gray-600 hover:text-template-primary p-1.5" title={data.contactEmail}>
                    <Mail className="h-5 w-5" />
                    <span className="ml-1.5 hidden text-sm md:inline">{data.contactEmail}</span>
                  </a>
                )}
                {data.phone && (
                  <a href={`tel:${(data.phoneDDI || '').replace(/\D/g, '')}${(data.phone || '').replace(/\D/g, '')}`} aria-label="Telefone de contato" className="flex items-center text-gray-600 hover:text-template-primary p-1.5" title={`${data.phoneDDI || ''} ${data.phone}`}>
                    <Phone className="h-5 w-5" />
                    <span className="ml-1.5 hidden text-sm md:inline">{`${data.phoneDDI || ''} ${data.phone}`}</span>
                  </a>
                )}
                {data.whatsapp && (
                  <a href={`https://wa.me/${(data.whatsappDDI || '').replace(/\D/g, '')}${(data.whatsapp || '').replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="flex items-center text-gray-600 hover:text-template-primary p-1.5" title={`${data.whatsappDDI || ''} ${data.whatsapp}`}>
                    <MessageCircle className="h-5 w-5" />
                    <span className="ml-1.5 hidden text-sm md:inline">{`${data.whatsappDDI || ''} ${data.whatsapp}`}</span>
                  </a>
                )}
              </div>
              {navLinks.length > 0 && (
                <div className="flex-shrink-0 sm:ml-2">
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button variant="outline" size="icon" style={{ borderColor: 'var(--template-primary-color)', color: 'var(--template-primary-color)' }} className="hover:bg-template-primary/10">
                        <MenuIcon className="h-6 w-6" />
                        <span className="sr-only">Abrir menu</span>
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-[280px] sm:w-[320px] bg-background p-0 text-foreground">
                      <SheetHeader className="border-b p-4">
                        <SheetTitle className="font-headline" style={{color: 'var(--template-primary-color)'}}>Menu</SheetTitle>
                      </SheetHeader>
                      <nav className="flex flex-col space-y-1 p-4">
                        {navLinks.map((link) => (
                          <SheetClose asChild key={link.href}>
                            <Link
                              href={link.href}
                              className="block rounded-md px-3 py-2 text-base font-medium hover:bg-muted hover:text-template-primary"
                            >
                              {link.label}
                            </Link>
                          </SheetClose>
                        ))}
                        <div className="border-t pt-4 mt-4 space-y-2 sm:hidden">
                            {data.contactEmail && (
                              <SheetClose asChild>
                                <a href={`mailto:${data.contactEmail}`} className="flex items-center gap-2 rounded-md px-3 py-2 text-base font-medium hover:bg-muted hover:text-template-primary">
                                    <Mail className="h-5 w-5 text-template-primary/80"/> Email
                                </a>
                              </SheetClose>
                            )}
                            {data.phone && (
                              <SheetClose asChild>
                                <a href={`tel:${(data.phoneDDI || '').replace(/\D/g, '')}${(data.phone || '').replace(/\D/g, '')}`} className="flex items-center gap-2 rounded-md px-3 py-2 text-base font-medium hover:bg-muted hover:text-template-primary">
                                    <Phone className="h-5 w-5 text-template-primary/80"/> Telefone
                                </a>
                              </SheetClose>
                            )}
                            {data.whatsapp && (
                              <SheetClose asChild>
                                <a href={`https://wa.me/${(data.whatsappDDI || '').replace(/\D/g, '')}${(data.whatsapp || '').replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-md px-3 py-2 text-base font-medium hover:bg-muted hover:text-template-primary">
                                    <MessageCircle className="h-5 w-5 text-template-primary/80"/> WhatsApp
                                </a>
                              </SheetClose>
                            )}
                        </div>
                      </nav>
                    </SheetContent>
                  </Sheet>
                </div>
              )}
            </div>
          </div>
        </header>

        <main>
          {sortedAndActiveSections.map(({ key, component: Component }) => <Component key={key} data={data} />)}
          <FooterSection data={data} />
        </main>
        
        {showWhatsappButton && (
          <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-green-500 text-white shadow-lg transition-transform hover:scale-110 flex items-center justify-center" aria-label="Fale conosco pelo WhatsApp" title="Fale conosco pelo WhatsApp">
            <MessageCircle className="h-8 w-8" />
          </a>
        )}
      </div>
    </>
  );
}
